/**
 * Chutt... - Brand Selector
 * Dropdown pour sélectionner la marque à surveiller
 * Subtle & Sophisticated design
 */
import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { colors } from '../../styles/colors';

// Couleurs par secteur - subtle
const SECTOR_COLORS = {
  telecom: '#374151',
  banque: '#2E9E6B',
  retail: '#6B7280',
  gouvernement: '#4B5563',
  autre: '#9CA3AF',
};

const SECTOR_LABELS = {
  telecom: 'Télécom',
  banque: 'Banque',
  retail: 'Retail',
  gouvernement: 'Gouv.',
  autre: 'Autre',
};

function BrandSelector({ brands, selectedBrand, onBrandChange, darkMode = false, compact = false }) {
  const currentBrand = brands?.find(b => b.id === selectedBrand);

  if (!brands || brands.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: darkMode ? 'rgba(255,255,255,0.5)' : colors.gray400 }}>
        <Typography variant="body2">Aucune marque configurée</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <FormControl size="small" sx={{ minWidth: compact ? 130 : 200 }}>
        <Select
          value={selectedBrand || ''}
          onChange={(e) => onBrandChange(e.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': 'Selectionner une marque' }}
          sx={{
            bgcolor: compact ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.1)' : colors.gray200),
            color: darkMode ? 'white' : colors.charcoal,
            borderRadius: compact ? '100px' : '12px',
            fontSize: compact ? '0.85rem' : undefined,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: compact ? colors.borderLight : (darkMode ? 'rgba(255,255,255,0.2)' : colors.gray200),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: compact ? colors.border : (darkMode ? 'rgba(255,255,255,0.4)' : colors.gray400),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: compact ? colors.primary : (darkMode ? colors.accent : colors.charcoal),
            },
            '& .MuiSvgIcon-root': {
              color: compact ? colors.textLight : (darkMode ? 'white' : colors.slate),
            },
          }}
          renderValue={(selected) => {
            const brand = brands.find(b => b.id === selected);
            if (!brand) return 'Sélectionner une marque';
            return (
              <Typography sx={{ color: darkMode ? 'white' : colors.charcoal }}>{brand.name}</Typography>
            );
          }}
        >
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ flex: 1, color: colors.charcoal }}>{brand.name}</Typography>
                {brand.sector && (
                  <Chip
                    label={SECTOR_LABELS[brand.sector] || brand.sector}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: SECTOR_COLORS[brand.sector] || SECTOR_COLORS.autre,
                      color: 'white',
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Afficher les mots-cles (hidden in compact mode) */}
      {!compact && currentBrand?.keywords && currentBrand.keywords.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {currentBrand.keywords.slice(0, 3).map((keyword, idx) => (
            <Chip
              key={idx}
              label={keyword}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : colors.gray200,
                color: darkMode ? 'rgba(255,255,255,0.7)' : colors.gray600,
              }}
            />
          ))}
          {currentBrand.keywords.length > 3 && (
            <Chip
              label={`+${currentBrand.keywords.length - 3}`}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : colors.gray200,
                color: darkMode ? 'rgba(255,255,255,0.5)' : colors.gray400,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default BrandSelector;
